/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from "react";
import { FormDialog } from "components/common_components/Dialogs";
import { Form as FormikForm, TextField, CheckboxField, DateTimePickerField, SelectField } from "components/common_components/Formik";
import { Grid } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormGroup from "@material-ui/core/FormGroup";
import { addTaskValidationSchema } from "./validationSchemas";
import AppContext from "AppContext";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import { dateTimeAdd, getDateMillis, getUnitsOfTime } from "utils/dateUtils";
import { hasElements } from "utils/arrays";
import UserSelect from "components/common_components/UserSelect/UserSelect";

export default function AddTaskDialog({ resources, onSuccessCallback, trnFlowId, ...formDialogProps }) {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.Login);
    const [initialValues, setInitialValues] = useState(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        api: {
            Workflows: { Tasks },
        },
    } = useContext(AppContext);

    useEffect(() => {
        const cleanUp = () => {
            setInitialValues(undefined);
        };
        if (formDialogProps.open) {
            setInitialValues({
                name: "",
                description: "",
                assigneeUser: getActiveUsers().find((x) => x.id === user.id),
                allowAssigneeReassign: false,
                dueDate: null,
                notifyBeforeDueDate: false,
                notifyBeforeDueDateInterval: 0,
                notifyBeforeDueDateTimeUnit: "",
                notifyBeforeDueDateUsers: [],
            });
        } else {
            cleanUp();
        }

        return cleanUp;
    }, [formDialogProps.open]);

    function onSuccess(sentPayload, setSubmitting) {
        setSubmitting(false);
        setIsSubmitting(false);
        dispatch(notifySuccess(`Task created successfully!`));
        formDialogProps.onClose();
        onSuccessCallback();
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            dispatch(notifyError(error));
            formDialogProps.onClose();
        }
        setSubmitting(false);
        setIsSubmitting(false);
    }

    function normalizeUsersToNotify(notifyBeforeDueDateUsers, assigneeUser) {
        const mailsTo = [];
        if (hasElements(notifyBeforeDueDateUsers)) {
            const filteredUsers = assigneeUser
                ? notifyBeforeDueDateUsers.filter((x) => x.id !== assigneeUser.id)
                : notifyBeforeDueDateUsers;
            mailsTo.push(...filteredUsers.map((x) => x.email));
        }
        return mailsTo;
    }

    function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const {
            assigneeUser,
            name,
            description,
            allowAssigneeReassign,
            dueDate,
            notifyBeforeDueDate,
            notifyBeforeDueDateInterval,
            notifyBeforeDueDateTimeUnit,
            notifyBeforeDueDateUsers,
        } = values;

        const assigneeUserId = assigneeUser ? assigneeUser.id : null;
        const dueDateInMillis = getDateMillis(dueDate);

        let dueDateNotificationDatetime = null;
        let dueDateNotificationConfig = null;

        if (dueDate && notifyBeforeDueDate) {
            dueDateNotificationDatetime = dateTimeAdd(
                dueDate,
                Number(notifyBeforeDueDateInterval) * -1,
                String(notifyBeforeDueDateTimeUnit),
            );
            dueDateNotificationConfig = JSON.stringify({
                mailsTo: normalizeUsersToNotify(notifyBeforeDueDateUsers, assigneeUser),
            });
        }

        const payloadToSend = {
            id: null,
            assigneeUserId,
            name,
            description,
            allowAssigneeReassign,
            dueDate: dueDateInMillis,
            trnFlowId: trnFlowId || null,
            notifyBeforeDueDate: notifyBeforeDueDate ? Boolean(notifyBeforeDueDate) : null,
            dueDateNotificationDatetime,
            dueDateNotificationConfig,
        };

        setSubmitting(true);
        setIsSubmitting(true);

        Tasks.add({
            payload: payloadToSend,
            onSuccess: () => onSuccess(payloadToSend, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    const getActiveUsers = () => {
        return [...(resources.users || [])].filter((u) => u.isActive);
    };

    function getAssigneeOptions() {
        let users = getActiveUsers();
        users.unshift({ id: null, name: "Unassigned" });
        return users;
    }

    return (
        <FormDialog {...formDialogProps} isSubmitting={isSubmitting}>
            {initialValues && (
                <FormikForm
                    id={`in-dialog-form-new-task`}
                    onSubmit={onSubmitHandler}
                    validateOnChange={false}
                    title="Add a new task"
                    initialValues={initialValues}
                    validationSchema={addTaskValidationSchema}
                    showButtons={false}
                >
                    {({ values }) => (
                        <Grid container spacing={3}>
                            <Grid item sm={12} md={6}>
                                <FormControl component="fieldset" fullWidth>
                                    <FormLabel component="legend">General information</FormLabel>
                                    <FormGroup row>
                                        <TextField fieldName="name" label="Name *" multiline minRows={3} />
                                        <TextField fieldName="description" label="Description" multiline minRows={10} />
                                        <UserSelect fieldName="assigneeUser" users={getAssigneeOptions()} label="Assignee User" />
                                        <CheckboxField fieldName="allowAssigneeReassign" label="Allow Assignee Reassign" />
                                        <DateTimePickerField fieldName="dueDate" label="Due Date" />
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                            <Grid item sm={12} md={6}>
                                <FormControl component="fieldset" fullWidth>
                                    <FormLabel component="legend">Notifications</FormLabel>
                                    <FormGroup row>
                                        <CheckboxField fieldName="notifyBeforeDueDate" label="Notify due date" />
                                        <TextField
                                            disabled={!Boolean(values.notifyBeforeDueDate)}
                                            fieldName="notifyBeforeDueDateInterval"
                                            label="Interval *"
                                            type="number"
                                        />
                                        <SelectField
                                            disabled={!Boolean(values.notifyBeforeDueDate)}
                                            fieldName="notifyBeforeDueDateTimeUnit"
                                            label="Unit of time *"
                                            options={getUnitsOfTime(true)}
                                        />
                                        <UserSelect
                                            fieldName="notifyBeforeDueDateUsers"
                                            disabled={!Boolean(values.notifyBeforeDueDate)}
                                            multiple
                                            limitTags={8}
                                            users={getActiveUsers()}
                                            label="Also notify user"
                                        />
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}
                </FormikForm>
            )}
        </FormDialog>
    );
}
